<script>
import { nativeApps } from '../configuration.js'
import analyticsService from '@services/analytics-service'
import { isIosBrowser, isInAppBrowser } from '@utils/user-agent'

const EXCLUDED_PATHS = ['/account']
const APP_STORE_URL = `https://apps.apple.com/app/${nativeApps.iosAppId}`

function _isExcludedPath(path) {
    return EXCLUDED_PATHS.some(p => path.startsWith(p))
}

export default {
    name: 'CustomNativeSmartBanner',
    data() {
        return {
            isVisible: false,
            isOnExcludedPage: _isExcludedPath(window.location.pathname),
        }
    },
    mounted() {
        this._unregisterHook = this.$router.beforeEach((to, from, next) => {
            this.isOnExcludedPage = _isExcludedPath(to.path)
            next()
        })

        if (isIosBrowser() && !isInAppBrowser() && !this.isOnExcludedPage) {
            this.isVisible = true
            analyticsService.customIosAppBannerView()
        }
    },
    beforeDestroy() {
        if (this._unregisterHook) {
            this._unregisterHook()
        }
    },
    methods: {
        dismissBanner() {
            this.isVisible = false
            analyticsService.customIosAppBannerDismiss()
        },
        navigateToApp() {
            analyticsService.customIosAppBannerClick()
            window.location.href = APP_STORE_URL
        },
    },
}
</script>

<template>
    <div v-if="!isOnExcludedPage">
        <transition
            :enter-active-class="$style.slideUpEnterActive"
            :leave-active-class="$style.slideUpLeaveActive"
            :enter-class="$style.slideUpEnter"
            :leave-to-class="$style.slideUpLeaveTo"
        >
            <div
                v-if="isVisible"
                :class="$style.banner"
            >
                <button
                    :class="$style.closeButton"
                    aria-label="Close"
                    @click="dismissBanner"
                >
                    <BaseIcon
                        name="xmark-circle"
                        :class="$style.closeIcon"
                    />
                </button>
                <span :class="$style.text">FRISKY is better in the app</span>
                <BaseButton
                    button-type="white"
                    :class="$style.openButton"
                    :modifiers="{ autoWidth: true, roundCorners: true, noMargins: true }"
                    @click="navigateToApp"
                >Open</BaseButton>
            </div>
        </transition>
    </div>
</template>

<style lang="scss" module>
@use '@design' as *;

.banner {
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: $layer-modal-z-index + 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: $s-l $s-m;
    padding-bottom: calc(#{$s-l} + env(safe-area-inset-bottom, 0px));
    background: $c-shark-grey;
    border-radius: 20px 20px 0 0;
}

.closeButton {
    flex-shrink: 0;
    padding: 0;
    cursor: pointer;
    background: none;
    border: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;
}

.closeIcon {
    fill: $c-white;

    @include square(30px);
}

.text {
    flex: 1;
    padding: 0 $s-s;
    font-size: 14px;
    font-weight: 500;
    color: $c-white;
    text-align: center;
}

.openButton[class] {
    min-width: 136px;
    padding: 0 $s-xxl;
    font-size: $s-m;
    font-weight: 700;
    border-radius: 29px;
}

.slideUpEnterActive,
.slideUpLeaveActive {
    transition: transform 0.3s ease;
}

.slideUpEnter,
.slideUpLeaveTo {
    transform: translateY(100%);
}
</style>
